import React, { useMemo, useLayoutEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Hooks } from "@xt/xtbase";
import step1Img from "@/assets/img/cfd-guide/guide1.png";
import lineImg from "@/assets/img/cfd-guide/line.png";
import store from "store";
import styles from "./StandardGuide.module.scss";

const { useTranslation } = Hooks;

export type StandardGuideProps = {
  onSkip: () => void;
  onNext: () => void;
};

const DESIGN_WIDTH = 375;

function getScale(): number {
  if (typeof window === "undefined") return 1;
  const w = window.innerWidth || DESIGN_WIDTH;
  return w / DESIGN_WIDTH;
}

/** 与 Header 内「交易对 + 下拉」行对齐，避免迁移后固定像素错位 */
const GUIDE_TARGET_SELECTOR = '[data-guide-target="cfdSymbolNameRow"]';
const StandardGuide: React.FC<StandardGuideProps> = ({ onSkip, onNext }) => {
  const t = useTranslation();
  const { name } = store.market;

  const scale = useMemo(() => getScale(), []);

  const [layout, setLayout] = useState<{
    highlight: { top: number; left: number; width: number; height: number };
    tip: { top: number; left: number };
  } | null>(null);

  const measure = useCallback(() => {
    if (typeof document === "undefined") return;
    const target = document.querySelector(GUIDE_TARGET_SELECTOR) as HTMLElement | null;
    if (!target) return;
    const tr = target.getBoundingClientRect();
    const top = tr.top;
    const left = tr.left;
    const w = tr.width;
    const h = tr.height;
    // 与 ssr-web-trade 设计稿一致：高亮底 111+39=150，提示框顶 208，间距 58px
    const gapBelowHighlight = 58 * scale;
    const tipTop = top + h + gapBelowHighlight;
    const tipLeft = 73 * scale;
    setLayout({
      highlight: { top, left, width: w, height: h },
      tip: { top: tipTop, left: tipLeft },
    });
  }, [scale]);

  useLayoutEffect(() => {
    measure();
    const target = document.querySelector(GUIDE_TARGET_SELECTOR) as HTMLElement | null;
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const ro =
      typeof ResizeObserver !== "undefined" && target
        ? new ResizeObserver(() => measure())
        : null;
    ro?.observe(target);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      ro?.disconnect();
    };
  }, [measure]);

  // 获取当前交易对名称
  const contractName = useMemo(() => {
    if (!name) return "BTC/USDT";
    const formattedName = store.market.formatCfdName(name);
    return formattedName;
  }, [name]);

  const imgSrc = (step1Img as any)?.src || (step1Img as any);
  const lineImgSrc = (lineImg as any)?.src || (lineImg as any);

  const hl = layout?.highlight;
  const tp = layout?.tip;

  return (
    <div style={{ ["--sg-scale" as any]: scale }}>
        {/* 高亮区域（选择交易对）：位置与 Header nameRow 测量对齐 */}
        <div
          className={styles.highlightRect}
          style={
            hl
              ? {
                  position: "fixed",
                  top: hl.top,
                  left: hl.left,
                  width: hl.width,
                  height: hl.height,
                  minWidth: hl.width,
                  zIndex: 10001,
                }
              : undefined
          }
        >
          {/* 高亮框内的交易对显示 */}
          <div className={styles.highlightContent}>
            <span className={styles.highlightText}>{contractName}</span>
            <span className={styles.highlightContract}>{t("cfd.contract")}</span>
          </div>
        </div>

        {/* 引导提示框 */}
        <div
          className={styles.tip}
          style={
            tp
              ? {
                  position: "fixed",
                  top: tp.top,
                  left: tp.left,
                  zIndex: 10001,
                }
              : undefined
          }
        >
          <div className={styles.tipBg}>
            <div className={styles.tipText}>
              <div className={styles.tipTitle}>{t("cfd.guide.standard.step1.title")}</div>
              <div className={styles.tipDesc}>{t("cfd.guide.standard.step1.desc")}</div>
            </div>
            <img className={styles.tipImg} src={imgSrc} alt="" />
          </div>
          <div className={styles.star1} />
          <div className={styles.star2} />
          <img className={styles.lineImg} src={lineImgSrc} alt="" />
        </div>

        {/* 底部按钮 */}
        <div className={styles.btnGroup}>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onNext}>
            {`${t("cfd.guide.next")} 1/7`}
          </button>
          <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onSkip}>
            {t("cfd.guide.skip")}
          </button>
      </div>
    </div>
  );
};

export default observer(StandardGuide);

